/**
 * Kamera-avlesning.
 *
 * Viser posisjon, zoom, pitch og bearing live, og kan kopiere gjeldende
 * kamera som en ferdig `map.jumpTo({...})`-blokk. Poenget er å kunne peke ut
 * kamerastater presist – «sånn her» blir til tall man kan lime inn i
 * CAMERA_STATES i camera.js.
 *
 * Verdiene leses i `move`-hendelsen, ikke i en egen animasjonsløkke: en
 * requestAnimationFrame-løkke ville tvunget fram repaint hele tiden og
 * konkurrert med flyTo, akkurat som kommentaren i models.js advarer mot.
 */

import { groundElevation } from './terrain.js';

const STORAGE_KEY = 'ruterracet.hud';

function row(label, value) {
  return `<dt>${label}</dt><dd>${value}</dd>`;
}

/**
 * @param {import('maplibre-gl').Map} map
 * @param {HTMLElement} panel   container for avlesningen
 * @param {HTMLElement} [toggle] knapp som slår panelet av og på
 */
export function createCameraHud(map, panel, toggle) {
  let visible = localStorage.getItem(STORAGE_KEY) !== 'off';

  function update() {
    if (!visible) return;
    const c = map.getCenter();
    const zoom = map.getZoom();
    const pitch = map.getPitch();
    const bearing = map.getBearing();
    const ground = groundElevation(map, [c.lng, c.lat]);

    panel.querySelector('dl').innerHTML = [
      row('senter lng', c.lng.toFixed(7)),
      row('senter lat', c.lat.toFixed(7)),
      row('zoom', zoom.toFixed(5)),
      row('pitch', `${pitch.toFixed(1)}°`),
      row('bearing', `${bearing.toFixed(1)}°`),
      row('senterhøyde', `${map.getCenterElevation().toFixed(3)} m`),
      row('terreng', ground == null ? '–' : `${ground.toFixed(1)} m`),
    ].join('');
  }

  function snippet() {
    const c = map.getCenter();
    // jumpTo støtter eksplisitt senterhøyde. flyTo i denne MapLibre-versjonen
    // beregner høyden fra terrenget og kan derfor endre utsnittet ved replay.
    return `map.jumpTo(${JSON.stringify({
      center: [c.lng, c.lat],
      zoom: map.getZoom(),
      pitch: map.getPitch(),
      bearing: map.getBearing(),
      roll: map.getRoll(),
      elevation: map.getCenterElevation(),
      padding: map.getPadding(),
    }, null, 2)});`;
  }

  function apply() {
    panel.hidden = !visible;
    if (toggle) {
      toggle.setAttribute('aria-pressed', String(visible));
      toggle.textContent = visible ? 'Skjul kamera' : 'Vis kamera';
    }
    localStorage.setItem(STORAGE_KEY, visible ? 'on' : 'off');
    update();
  }

  panel.innerHTML = '<dl></dl><button type="button" class="hud-copy">Kopier kamera</button>';
  const copy = panel.querySelector('.hud-copy');
  copy.title = 'Kopier nøyaktig utsnitt med senterhøyde som map.jumpTo';
  copy.addEventListener('click', async () => {
    const text = snippet();
    try {
      await navigator.clipboard.writeText(text);
      copy.textContent = 'Kopiert ✓';
    } catch {
      // Utklippstavla krever sikker kontekst og brukertillatelse; logg i
      // stedet så verdiene uansett er tilgjengelige.
      console.log(text);
      copy.textContent = 'I konsollen';
    }
    setTimeout(() => { copy.textContent = 'Kopier kamera'; }, 1400);
  });

  toggle?.addEventListener('click', () => { visible = !visible; apply(); });

  map.on('move', update);
  // Terrenghøyden er null til DEM-flisen er lastet; oppdater når den kommer.
  map.on('idle', update);
  apply();

  return { update, snippet };
}
